import { buildOpaqueDailyLoginPath, isDailyAbsiteEnabled } from './daily-question-opaque';
import { DEFAULT_POST_LOGIN_PATH } from '../constants/postLoginPaths';
import { toOpaqueQuizLinkPath } from './url-secret-scrub';

// Google sign-in uses a same-tab redirect: the browser leaves /login for the
// provider and comes back to /socialAuth, so /login's ?next= does not survive
// the round trip. /login stashes the intended post-login destination here
// before it leaves and restores it when the sign-in finishes.
// The stash is strictly one-round-trip scoped (gate B2): it is timestamped on
// write, ignored/cleared past a short TTL, and swept on any /login mount that
// is NOT completing an OAuth return — so an attempt aborted at Google can never
// misroute a later (e.g. phone) login.
export const OAUTH_RETURN_PATH_KEY = 'oauthReturnPath';
export const OAUTH_RETURN_PATH_AT_KEY = 'oauthReturnPathAt';
export const OAUTH_RETURN_PATH_MAX_AGE_MS = 10 * 60 * 1000; // one OAuth round-trip, generously

export const clearOAuthReturnPath = () => {
  window.localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
  window.localStorage.removeItem(OAUTH_RETURN_PATH_AT_KEY);
};

// Read-and-consume the stashed destination; null unless it was written within
// the TTL (a bare or stale stash is cleared, never honored).
export const takeOAuthReturnPath = () => {
  const stashed = window.localStorage.getItem(OAUTH_RETURN_PATH_KEY);
  const stashedAt = Number(window.localStorage.getItem(OAUTH_RETURN_PATH_AT_KEY));
  clearOAuthReturnPath();
  if (stashed?.startsWith('/daily-absite-question') && !isDailyAbsiteEnabled()) {
    return null;
  }
  if (stashed && stashedAt && (Date.now() - stashedAt) < OAUTH_RETURN_PATH_MAX_AGE_MS) {
    return stashed;
  }
  return null;
};

const DAILY_PATH = '/daily-absite-question';

// The /login address that sends the student on to `path` once they sign in,
// for a sign-in that did not finish and is tried again. `path` is a stashed
// destination (takeOAuthReturnPath), written the way /login itself reads it:
//   - the Daily question in its opaque sign-in form (buildOpaqueDailyLoginPath);
//   - a quiz link by its token-free claim path (a token is moved to the tab);
//   - any other page as ?next=, which /login checks against its allowlist.
// Anything that is not a same-site path gives plain /login (`//host` and
// `/\host` resolve to another host, as a browser would read them), and so does
// the default destination: a sign-in started from a bare /login stashed it, and
// /login goes there anyway. ?next= would greet the student with "Sign in to
// continue where you left off." when they never left anywhere.
const SAME_SITE_BASE = 'https://qlearn.local';

export const loginPathReturningTo = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/')) return '/login';
  let parsed;
  try {
    parsed = new URL(path, SAME_SITE_BASE);
  } catch (e) {
    return '/login';
  }
  if (parsed.origin !== SAME_SITE_BASE || path === DEFAULT_POST_LOGIN_PATH) return '/login';
  if (parsed.pathname.toLowerCase() === DAILY_PATH) {
    if (!isDailyAbsiteEnabled()) return '/login';
    const params = parsed.searchParams;
    return buildOpaqueDailyLoginPath({
      date: params.get('date'),
      revisionId: params.get('revisionId'),
      revisionHash: params.get('revisionHash'),
      touchId: params.get('touch'),
      grantId: params.get('grant'),
    });
  }
  return `/login?next=${encodeURIComponent(toOpaqueQuizLinkPath(path))}`;
};
